import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { CalendarDays, Plus, Clock, AlertTriangle, ShieldCheck, TrendingDown, MessageSquare, X, Send, Download } from "lucide-react";
import { exportCsv } from "../lib/exportCsv";

const statusBadge = (status, t) => {
  const map = {
    pending: ["sw-badge-pending", t("status.pending")],
    approved: ["sw-badge-approved", t("status.approved")],
    rejected: ["sw-badge-rejected", t("status.rejected")],
  };
  const [cls, lbl] = map[status] || ["sw-badge-neutral", status];
  return <span className={`sw-badge ${cls}`}>{lbl}</span>;
};

const daysToExpire = (iso) => {
  if (!iso) return null;
  try {
    const ms = new Date(iso).getTime() - Date.now();
    return Math.floor(ms / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
};

function BalanceCard({ kind, balance, expiresAt, accent }) {
  const days = daysToExpire(expiresAt);
  const expired = days != null && days < 0;
  const soon = days != null && days >= 0 && days <= 60;
  return (
    <div className={`sw-card relative overflow-hidden ${expired ? "border-[#e5c1c1]" : ""}`}>
      {accent && (
        <div
          className="absolute top-0 left-0 right-0 h-1"
          style={{ background: accent }}
        />
      )}
      <div className="flex items-start justify-between mb-3">
        <span className="sw-label !mb-0">{kind}</span>
        <Clock size={16} className="text-[#475569]" />
      </div>
      <div className="flex items-baseline gap-2 mb-3">
        <span className="text-[48px] font-semibold text-[#323232] leading-none tracking-tight">{balance}</span>
        <span className="text-[14px] text-[#747474]">days</span>
      </div>
      {expiresAt && (
        <div className="text-[12px] flex items-center gap-1.5">
          {expired ? (
            <span className="sw-badge sw-badge-rejected"><AlertTriangle size={12} /> EXPIRED</span>
          ) : soon ? (
            <span className="sw-badge sw-badge-pending"><AlertTriangle size={12} /> {days} days left</span>
          ) : (
            <span className="text-[#747474]">Expires in {days} days · {expiresAt.split("T")[0]}</span>
          )}
        </div>
      )}
    </div>
  );
}

export default function PTOPage() {
  const { t } = useI18n();
  const { user, refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [rule, setRule] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ type: "PTO", start_date: "", end_date: "", days: 1, reason: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    const [r1, r2] = await Promise.all([api.get("/pto"), api.get("/pto/my-rule").catch(() => ({ data: null }))]);
    setItems(r1.data);
    setRule(r2.data);
  };

  useEffect(() => { load(); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const days = parseFloat(form.days);
    const bal = form.type === "PTO" ? user?.pto_balance : user?.vacation_balance;
    if (days > bal) {
      setError(t("pto.insufficient"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/pto", { ...form, days });
      setShowForm(false);
      setForm({ type: "PTO", start_date: "", end_date: "", days: 1, reason: "" });
      await load();
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-[14px] bg-[#e2e8f0] text-[#475569] flex items-center justify-center">
            <CalendarDays size={22} />
          </div>
          <div>
            <h1 className="sw-heading text-[28px]">{t("pto.title")}</h1>
            <p className="text-[14px] text-[#747474]">{user?.location} · {user?.contract_type?.replace("_", " · ")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="pto-export-csv"
            onClick={() => {
              const rows = items.map((r) => ({
                Type: r.type,
                "Start date": r.start_date,
                "End date": r.end_date,
                Days: r.days,
                Reason: r.reason || "",
                Status: r.status,
                Approver: r.approver_id || "",
                "Decided at": r.decided_at?.slice(0, 10) || "",
                "Decision comment": r.decision_comment || "",
              }));
              exportCsv(`pto_${new Date().toISOString().slice(0, 10)}.csv`, rows);
            }}
            className="sw-btn-secondary"
          >
            <Download size={14} /> Export CSV
          </button>
          <button data-testid="pto-new-btn" onClick={() => setShowForm(!showForm)} className="sw-btn-primary">
            <Plus size={16} /> {t("pto.new")}
          </button>
        </div>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <BalanceCard
          kind={t("pto.balance_pto")}
          balance={user?.pto_balance ?? 0}
          expiresAt={user?.pto_expires_at}
          accent="linear-gradient(90deg, #94a3b8, #475569)"
        />
        <BalanceCard
          kind={t("pto.balance_vac")}
          balance={user?.vacation_balance ?? 0}
          expiresAt={user?.vacation_expires_at}
          accent="linear-gradient(90deg, #b8e6cd, #34c759)"
        />
      </div>

      {/* Applicable rule */}
      {rule && (
        <div className="sw-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-[12px] bg-[#e2e8f0] text-[#475569] flex items-center justify-center shrink-0">
              <ShieldCheck size={18} />
            </div>
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-wider text-[#475569] mb-1">{t("pto.my_rule")}</div>
              <div className="font-medium text-[15px] text-[#323232]">{rule.location}{rule.contract_type ? ` · ${rule.contract_type.replace("_", " · ")}` : ""}</div>
              {rule.description && <div className="text-[13px] text-[#747474] mt-1">{rule.description}</div>}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                <div>
                  <div className="sw-label">{t("pto.rule_annual")} PTO</div>
                  <div className="text-[15px] font-medium text-[#323232]">{rule.pto_annual_days} d</div>
                </div>
                <div>
                  <div className="sw-label">{t("pto.rule_annual")} Vacation</div>
                  <div className="text-[15px] font-medium text-[#323232]">{rule.vacation_annual_days} d</div>
                </div>
                <div>
                  <div className="sw-label">{t("pto.rule_carryover")}</div>
                  <div className="text-[15px] font-medium text-[#323232]">{rule.vacation_carryover_max} d</div>
                </div>
                <div>
                  <div className="sw-label">{t("pto.rule_accrual")}</div>
                  <div className="text-[15px] font-medium text-[#323232] capitalize">{rule.accrual_method}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <form onSubmit={onSubmit} className="sw-card grid grid-cols-1 md:grid-cols-2 gap-4 sw-fade-up">
          <div>
            <label className="sw-label">{t("pto.type")}</label>
            <select data-testid="pto-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="sw-input">
              <option value="PTO">PTO ({user?.pto_balance} d available)</option>
              <option value="Vacation">Vacation ({user?.vacation_balance} d available)</option>
            </select>
          </div>
          <div>
            <label className="sw-label">{t("pto.days")}</label>
            <input data-testid="pto-days" type="number" step="0.5" min="0.5" value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })} className="sw-input" required />
          </div>
          <div>
            <label className="sw-label">{t("pto.start")}</label>
            <input data-testid="pto-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="sw-input" required />
          </div>
          <div>
            <label className="sw-label">{t("pto.end")}</label>
            <input data-testid="pto-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="sw-input" required />
          </div>
          <div className="md:col-span-2">
            <label className="sw-label">{t("pto.reason")}</label>
            <textarea data-testid="pto-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={3} className="sw-input" />
          </div>
          {error && (
            <div className="md:col-span-2 text-[13px] text-[#881a27] bg-[#e5c1c1] rounded-[10px] px-4 py-2 flex items-center gap-2">
              <TrendingDown size={14} /> {error}
            </div>
          )}
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => setShowForm(false)} className="sw-btn-secondary">{t("common.cancel")}</button>
            <button data-testid="pto-submit-btn" type="submit" disabled={submitting} className="sw-btn-primary">
              {submitting ? t("common.loading") : t("pto.submit")}
            </button>
          </div>
        </form>
      )}

      <div>
        <h2 className="sw-heading text-[18px] mb-4 flex items-center gap-2"><Clock size={18} /> {t("pto.history")}</h2>
        {items.length === 0 ? (
          <div className="sw-card text-[#747474] text-center py-12">{t("pto.empty")}</div>
        ) : (
          <div className="sw-card !p-0 overflow-hidden">
            <table className="w-full text-[14px]">
              <thead className="bg-[#f6f6f6] text-[#747474] text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-5 py-3">{t("pto.type")}</th>
                  <th className="text-left px-5 py-3">{t("pto.start")}</th>
                  <th className="text-left px-5 py-3">{t("pto.end")}</th>
                  <th className="text-left px-5 py-3">{t("pto.days")}</th>
                  <th className="text-left px-5 py-3">{t("common.status")}</th>
                  <th className="text-left px-5 py-3"><MessageSquare size={12} className="inline mr-1" />Comments</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr
                    key={it.id}
                    data-testid={`pto-row-${it.id}`}
                    className="border-t border-[#eeeded] hover:bg-[#f6f6f6] cursor-pointer"
                    onClick={() => setDetail(it)}
                  >
                    <td className="px-5 py-4 text-[#323232]">{it.type}</td>
                    <td className="px-5 py-4 text-[#323232]">{it.start_date}</td>
                    <td className="px-5 py-4 text-[#323232]">{it.end_date}</td>
                    <td className="px-5 py-4 text-[#323232]">{it.days}</td>
                    <td className="px-5 py-4">{statusBadge(it.status, t)}</td>
                    <td className="px-5 py-4 text-[#747474] text-[12px]">
                      <MessageSquare size={12} className="inline mr-1" />
                      {it.comments?.length || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detail && (
        <PTODetailModal
          req={detail}
          currentUser={user}
          onClose={() => setDetail(null)}
          onUpdated={async (updated) => {
            setDetail(updated);
            await load();
          }}
        />
      )}
    </div>
  );
}

function PTODetailModal({ req, currentUser, onClose, onUpdated }) {
  const { t } = useI18n();
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setPosting(true);
    try {
      const r = await api.post(`/pto/${req.id}/comment`, { comment: text });
      setText("");
      onUpdated(r.data);
    } catch (err) {
      alert(err?.response?.data?.detail || "Error");
    } finally {
      setPosting(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] max-w-[600px] w-full p-6 shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.12em] text-[#475569]">{req.type}</div>
            <h3 className="sw-heading text-[20px] mt-1">{req.start_date} → {req.end_date} · {req.days}d</h3>
            <div className="text-[12px] text-[#747474] italic mt-1">{req.reason || "—"}</div>
          </div>
          <button onClick={onClose} className="text-[#94a3b8]" data-testid="pto-detail-close"><X size={20} /></button>
        </div>
        <div className="border-t border-[#e2e8f0] pt-4">
          <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#475569] mb-3 flex items-center gap-2"><MessageSquare size={12} /> Discussion ({req.comments?.length || 0})</div>
          <div className="flex flex-col gap-3 mb-4 max-h-[280px] overflow-y-auto">
            {(req.comments || []).length === 0 ? (
              <div className="text-[12px] text-[#94a3b8] text-center py-6">{t("pto.no_comments")}</div>
            ) : (
              req.comments.map((c) => {
                const mine = c.user_id === currentUser?.id;
                return (
                  <div key={c.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
                    <div className={`max-w-[80%] rounded-[14px] px-3 py-2 text-[13px] ${mine ? "bg-[#475569] text-white" : "bg-[#f1f5f9] text-[#1e293b]"}`}>
                      {c.comment}
                    </div>
                    <div className="text-[10px] text-[#94a3b8] mt-1">{c.user_name} · {c.user_role} · {c.created_at?.slice(0,16).replace("T"," ")}</div>
                  </div>
                );
              })
            )}
          </div>
          <form onSubmit={submit} className="flex items-end gap-2">
            <textarea
              data-testid="pto-comment-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={2}
              placeholder={t("common.write_comment")}
              className="sw-input flex-1 resize-none"
            />
            <button data-testid="pto-comment-submit" type="submit" disabled={posting || !text.trim()} className="sw-btn-primary !px-3 !py-2 h-fit">
              <Send size={14} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
